import { BookingFormValidator } from "../events/booking-form-validator";
export interface PaymentOptions {
    formId: string;
    keyId?: string;
    contactId?: string;
    applicationId?: string;
    successUrl: string;
    failureUrl: string;
    total: string | number;
    item: string;
    invoiceGuid?: string;
}

export interface StripeResponse {
    clientSecret: string;
    publicApiKey: string;
    amount: number;
}

declare global {
    const Stripe: any;
    interface Window {
        initializeEventBookingPayment: Function;
        initializeCourseBookingPayment: Function;
        initializeMembershipApplicationPayment: Function;
        initializeECommercePayment: Function;
    }
}

export class PaymentService {
    private stripe: any;
    private elements: any;
    private options: PaymentOptions | null = null;

    // Kept so the demo's test-card route can confirm the same intent the
    // Payment Element is holding, without going through its fields.
    private clientSecret: string | null = null;
    private useTestCard = false;

    public async initializeECommercePayment(
        formId: string,
        contactId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string,
        invoiceGuid?: string
    ): Promise<void> {
        this.options = { formId, keyId: contactId, successUrl, failureUrl, total, item, invoiceGuid };
        document.querySelector("#payment-form")
            ?.addEventListener("submit", this.handleSubmitECommerce.bind(this));

        const voucherCode = (document.getElementById("DiscountCode") as HTMLInputElement | null)?.value?.trim() || undefined;
        await this.mountStripeElement("Shop Checkout", total, item, "", voucherCode, invoiceGuid);
        await this.checkStatus();
    }

    private async handleSubmitECommerce(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!keyId || !successUrl || !failureUrl) return;

        await this.submitECommerceWithStripe(formId, keyId, successUrl, failureUrl);
    }

    private async submitECommerceWithStripe(
        formId: string,
        keyId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId, "AgreeToTermsAndConditions");
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            `/umbraco/surface/StripeSurface/SaveEcommerceFormToSession?key=${keyId}`
        );
        if (!saved) return;

        const url = new URL("/umbraco/surface/ECommerceSurface/ConfirmAndComplete", window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        await this.confirmStripePayment(url.toString());
    }


    public async initializeEventBookingPayment(
        formId: string,
        eventId: string,
        eventBookingId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {
        this.options = { formId, keyId: eventId, successUrl, failureUrl, total, item };
        document.querySelector("#payment-form")
            ?.addEventListener("submit", this.handleSubmitEventBooking.bind(this));
        await this.mountStripeElement("Event Booking", total, item, eventBookingId);
        await this.checkStatus();
    }

    public async initializeCourseBookingPayment(
        formId: string,
        courseId: string,
        courseBookingId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {
        this.options = { formId, keyId: courseId, successUrl, failureUrl, total, item };
        document.querySelector("#payment-form")
            ?.addEventListener("submit", this.handleSubmitCourseBooking.bind(this));
        await this.mountStripeElement("Course Booking", total, item, courseBookingId);
        await this.checkStatus();
    }

    /**
     * Asks the server for a payment intent.
     *
     * GetPaymentIntent answers 204 with an empty body whenever it cannot create
     * one, so the JSON parse used to throw and take the whole initialisation
     * down with it - leaving no card fields and a "Pay now" button wired to an
     * unloaded Stripe. Anything short of a usable intent is reported here and
     * the button taken out of use, so the step fails visibly instead of hanging.
     */
    private async fetchPaymentIntent(url: string): Promise<StripeResponse | null> {
        let intent: StripeResponse | null = null;

        try {
            const response = await fetch(url, { method: "POST" });

            if (response.ok && response.status !== 204) {
                intent = await response.json();
            }
        } catch {
            intent = null;
        }

        if (!intent?.clientSecret || !intent?.publicApiKey) {
            this.disablePayment(
                "We could not start this payment. Please refresh the page, and contact us if it happens again."
            );

            return null;
        }

        return intent;
    }

    private mountPaymentElement(intent: StripeResponse): void {
        this.clientSecret = intent.clientSecret;
        this.stripe = Stripe(intent.publicApiKey);
        this.elements = this.stripe.elements({
            appearance: { theme: "stripe" },
            clientSecret: intent.clientSecret
        });
        this.elements.create("payment", { layout: "tabs" }).mount("#payment-element");
    }

    /** Leaves the message on screen - unlike showMessage, which clears itself. */
    private disablePayment(messageText: string): void {
        const submitButton = document.querySelector("#submit") as HTMLButtonElement | null;
        if (submitButton) submitButton.disabled = true;

        const messageContainer = document.querySelector("#payment-message");
        if (messageContainer) {
            messageContainer.classList.remove("hidden");
            messageContainer.textContent = messageText;
        }
    }

    private async mountStripeElement(
        userJourney: string,
        total: string | number,
        item: string,
        bookingId: string = "",
        voucherCode?: string,
        invoiceGuid?: string
    ): Promise<void> {
        const isCourse = userJourney === "Course Booking";
        const bookingIdParam = bookingId
            ? (isCourse
                ? `&courseBookingId=${encodeURIComponent(bookingId)}`
                : `&eventBookingId=${encodeURIComponent(bookingId)}`)
            : "";

        const voucherParam = voucherCode ? `&voucherCodeId=${encodeURIComponent(voucherCode)}` : "";
        const invoiceParam = invoiceGuid ? `&invoiceGuid=${encodeURIComponent(invoiceGuid)}` : "";

        const intent = await this.fetchPaymentIntent(
            `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${total}` +
            `&itemName=${encodeURIComponent(item)}` +
            `&userJourney=${encodeURIComponent(userJourney)}` +
            bookingIdParam +
            voucherParam +
            invoiceParam
        );

        if (!intent) return;

        this.mountPaymentElement(intent);
    }

    /**
     * Core invoice submission shared by event and course paths.
     * Callers supply explicit endpoints rather than a boolean flag.
     */
    private async submitInvoiceCore(
        formId: string,
        keyId: string,
        saveEndpoint: string | undefined,
        crmUrl: string,
        successUrl: string,
        failureUrl: string,
        poNumber: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);

        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            saveEndpoint
        );
        if (!saved) return;

        const url = new URL(crmUrl, window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "invoice");
        url.searchParams.set("poNumber", poNumber);
        window.location.href = url.toString();
    }

    /**
     * Validates and clears the PO Number input.
     * Returns the trimmed value on success, or null if validation failed.
     */
    private validatePoNumber(): string | null {
        const poInput = document.getElementById("PoNumber") as HTMLInputElement | null;
        const poError = document.getElementById("PoNumberError") as HTMLElement | null;
        const poNumber = (poInput?.value ?? "").trim();

        if (!poNumber) {
            if (poInput) { poInput.classList.add("input-validation-error"); poInput.focus(); }
            if (poError) { poError.textContent = "Please enter a PO number."; poError.classList.remove("d-none"); }
            return null;
        }

        if (poInput) poInput.classList.remove("input-validation-error");
        if (poError) { poError.textContent = ""; poError.classList.add("d-none"); }
        return poNumber;
    }

    public async submitEventInvoiceFromButton(btn: HTMLElement): Promise<void> {
        const poNumber = this.validatePoNumber();
        if (!poNumber) return;

        await this.submitInvoiceCore(
            "EventBookingForm",
            btn.getAttribute("data-event-id") ?? "",
            undefined,
            "/umbraco/surface/EventBookingSurface/SaveEventBookingToCrm",
            btn.getAttribute("data-success-url") ?? "",
            btn.getAttribute("data-failure-url") ?? "",
            poNumber
        );
    }

    public async submitCourseInvoiceFromButton(btn: HTMLElement): Promise<void> {
        const poNumber = this.validatePoNumber();
        if (!poNumber) return;

        const courseId = btn.getAttribute("data-course-id") ?? "";

        await this.submitInvoiceCore(
            "CourseBookingForm",
            courseId,
            `/umbraco/surface/StripeSurface/SaveCourseBookingFormToSession?key=${courseId}`,
            "/umbraco/surface/CourseBookingSurface/SaveCourseBookingToCrm",
            btn.getAttribute("data-success-url") ?? "",
            btn.getAttribute("data-failure-url") ?? "",
            poNumber
        );
    }

    public async initializeMembershipApplicationPayment(
        formId: string,
        contactId: string,
        applicationId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {
        await this.checkStatus();

        this.options = {
            formId: formId,
            contactId: contactId,
            applicationId: applicationId,
            successUrl: successUrl,
            failureUrl: failureUrl,
            total: total,
            item: item
        };

        const form = document.querySelector("#payment-form");
        form?.addEventListener("submit", this.handleSubmitMembershipApplication.bind(this));

        const intent = await this.fetchPaymentIntent(
            `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${this.options.total}` +
            `&itemName=${encodeURIComponent(this.options.item)}` +
            `&applicationId=${encodeURIComponent(this.options.applicationId ?? "")}` +
            `&userJourney=${encodeURIComponent("Membership Application")}`
        );

        if (!intent) return;

        this.mountPaymentElement(intent);
    }

    private async handleSubmitEventBooking(e: Event): Promise<void> {
        e.preventDefault();
        if (!this.options?.keyId) return;
        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!successUrl || !failureUrl) return;
        await this.submitEventCardPayment(formId, keyId, successUrl, failureUrl);
    }

    private async handleSubmitCourseBooking(e: Event): Promise<void> {
        e.preventDefault();
        if (!this.options?.keyId) return;
        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!successUrl || !failureUrl) return;
        await this.submitCourseCardPayment(formId, keyId, successUrl, failureUrl);
    }

    private async submitEventCardPayment(
        formId: string,
        keyId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form)
        );
        if (!saved) return;

        const url = new URL("/umbraco/surface/EventBookingSurface/SaveEventBookingToCrm", window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "card");
        await this.confirmStripePayment(url.toString());
    }

    private async submitCourseCardPayment(
        formId: string,
        keyId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            `/umbraco/surface/StripeSurface/SaveCourseBookingFormToSession?key=${keyId}`
        );
        if (!saved) return;

        const url = new URL("/umbraco/surface/CourseBookingSurface/SaveCourseBookingToCrm", window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "card");
        await this.confirmStripePayment(url.toString());
    }

    private async confirmStripePayment(returnUrl: string): Promise<void> {
        await this.confirmWithStripe(returnUrl);
    }

    private async handleSubmitMembershipApplication(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const appForm = document.getElementById(this.options.formId) as HTMLFormElement;

        const returnUrl =
            `${window.location.href.split("/").slice(0, 3).join("/")}/umbraco/surface/MembershipApplicationSurface/ConfirmStripePayment?key=${this.options.contactId}&successUrl=${encodeURIComponent(this.options.successUrl)}&failureUrl=${encodeURIComponent(this.options.failureUrl)}`;

        // Validate form
        if (!appForm.checkValidity()) {
            appForm.reportValidity();
            return;
        } else {
            // save the form to session
            try {
                const form = document.getElementById(this.options.formId) as HTMLFormElement;
                const formData = JSON.stringify(this.getFormData(form));

                const response = await fetch("/umbraco/surface/MembershipApplicationSurface/SavePaymentStep", {
                    method: "POST",
                    body: formData,
                    headers: {
                        "Content-Type": "application/json"
                    },
                    cache: 'no-cache'
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

            } catch (err) {
                console.error(err);
                return;
            }
        }

        await this.confirmWithStripe(returnUrl);
    }

    /**
     * Pays with Stripe's test card, for demo boxes.
     *
     * The card fields live in a cross-origin iframe, so nothing on our side can
     * type into them - that is Stripe's boundary and the point of it. What can be
     * done is skip the fields: Stripe accepts its own test tokens against a
     * publishable key, so the intent the Payment Element is already holding gets
     * confirmed with tok_visa rather than with typed input.
     *
     * It goes through the page's own submit, so the form is validated and saved to
     * session first, exactly as pressing Pay now would - and it confirms the same
     * intent, so everything afterwards runs unchanged.
     */
    public async payWithTestCard(): Promise<void> {
        this.useTestCard = true;

        const form = document.querySelector<HTMLFormElement>('#payment-form');
        form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    /**
     * Confirms the held intent with a test token, then goes where Stripe's own
     * redirect would have gone, carrying the parameters the return leg reads.
     */
    private async confirmWithTestCard(returnUrl: string): Promise<void> {
        if (!this.stripe || !this.clientSecret) {
            this.showMessage("This payment form did not load.");
            return;
        }

        const method = await this.stripe.createPaymentMethod({
            type: 'card',
            card: { token: 'tok_visa' }
        });

        if (method.error) {
            this.showMessage(method.error.message ?? "The test card was not accepted.");
            return;
        }

        const result = await this.stripe.confirmCardPayment(this.clientSecret, {
            payment_method: method.paymentMethod.id
        });

        if (result.error) {
            this.showMessage(result.error.message ?? "The test payment did not go through.");
            return;
        }

        const url = new URL(returnUrl, window.location.origin);
        url.searchParams.set('payment_intent', result.paymentIntent.id);
        url.searchParams.set('payment_intent_client_secret', this.clientSecret);
        url.searchParams.set('redirect_status', result.paymentIntent.status);

        window.location.href = url.toString();
    }


    /**
     * Confirms with Stripe and reports whatever comes back.
     *
     * A card payment normally leaves the page for return_url and nothing after
     * the await runs. Every other outcome ends up here, including the two that
     * used to leave the spinner turning: Stripe never having loaded, and a
     * confirmation that resolves with no error to read.
     */
    private async confirmWithStripe(returnUrl: string): Promise<void> {
        if (!this.stripe || !this.elements) {
            this.disablePayment(
                "This payment form did not load. Please refresh the page, and contact us if it happens again."
            );

            return;
        }

        this.setLoading(true);

        try {
            if (this.useTestCard) {
                this.useTestCard = false;
                await this.confirmWithTestCard(returnUrl);
                return;
            }

            const { error } = await this.stripe.confirmPayment({
                elements: this.elements,
                confirmParams: { return_url: returnUrl }
            });

            if (error) {
                this.showMessage(error.message ?? "An unexpected error occurred.");
            }
        } catch (err) {
            console.error(err);
            this.showMessage("An unexpected error occurred.");
        } finally {
            this.setLoading(false);
        }
    }

    // Fetches the payment intent status after payment submission
    private async checkStatus(): Promise<void> {
        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret) {
            return;
        }

        const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret);

        switch (paymentIntent.status) {
            case "succeeded":
                this.showMessage("Payment succeeded!");
                break;
            case "processing":
                this.showMessage("Your payment is processing.");
                break;
            case "requires_payment_method":
                this.showMessage("Your payment was not successful, please try again.");
                break;
            default:
                this.showMessage("Something went wrong.");
                break;
        }
    }


    public handleSubmitVoucherCode(e: Event): void {
        e.preventDefault();
        const input = document.getElementById("ecommDiscountInput") as HTMLInputElement | null;
        if (!input) return;
        const code = input.value.trim();
        if (!code) return;

        this.submitVoucherCode(code);
    }

    private async submitVoucherCode(code: string): Promise<void> {
        const msg = document.getElementById("voucherMessage");
        if (msg) msg.style.display = "none";

        // Get formId and keyId from the current options
        const formId = this.options?.formId;
        const keyId = this.options?.keyId;

        if (!formId || !keyId) {
            if (msg) {
                msg.textContent = "Unable to save form: missing form or key.";
                msg.className = "text-danger";
                msg.style.display = "block";
            }
            return;
        }

        try {
            // Save the form to session before applying the voucher
            const saved = await BookingFormValidator.saveFormToSession(
                formId,
                keyId,
                (form: HTMLFormElement) => this.getBookingFormData(form),
                `/umbraco/surface/StripeSurface/SaveEcommerceFormToSession?key=${keyId}`
            );

            if (!saved) {
                if (msg) {
                    msg.textContent = "Failed to save form before applying voucher.";
                    msg.className = "text-danger";
                    msg.style.display = "block";
                }
                return;
            }

            // Now apply the voucher
            const response = await fetch("/umbraco/surface/ECommerceSurface/ApplyStoreDiscount", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code }),
            });

            const result = await response.json();

            if (msg) {
                msg.textContent = result.message;
                msg.className = result.success ? "text-success" : "text-danger";
                msg.style.display = "block";
            }

            if (result.success) {
                window.location.reload();
            }
        } catch {
            if (msg) {
                msg.textContent = "Something went wrong. Please try again.";
                msg.className = "text-danger";
                msg.style.display = "block";
            }
        }
    }

    public async handleRemoveVoucherCode(e: Event): Promise<void> {
        e.preventDefault();

        const formId = this.options?.formId;
        const keyId = this.options?.keyId;

        if (formId && keyId) {
            await BookingFormValidator.saveFormToSession(
                formId,
                keyId,
                (form: HTMLFormElement) => this.getBookingFormData(form),
                `/umbraco/surface/StripeSurface/SaveEcommerceFormToSession?key=${keyId}`
            );
        }

        try {
            const response = await fetch("/umbraco/surface/ECommerceSurface/RemoveStoreDiscount", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });

            const result = await response.json();

            if (result.success) {
                window.location.reload();
            }
        } catch {
            console.error("Failed to remove store discount");
        }
    }

    // ------- UI helpers -------

    

    private showMessage(messageText: string): void {
        const messageContainer = document.querySelector("#payment-message");

        if (messageContainer) {
            messageContainer.classList.remove("hidden");
            messageContainer.textContent = messageText;

            setTimeout(() => {
                messageContainer.classList.add("hidden");
                messageContainer.textContent = "";
            }, 4000);
        }
    }

    // Show a spinner on payment submission
    private setLoading(isLoading: boolean): void {
        const submitButton = document.querySelector("#submit") as HTMLButtonElement;
        const spinner = document.querySelector("#spinner");
        const buttonText = document.querySelector("#button-text");

        if (isLoading) {
            // Disable the button and show a spinner
            if (submitButton) submitButton.disabled = true;
            spinner?.classList.remove("d-none");
            buttonText?.classList.add("hidden");
        } else {
            if (submitButton) submitButton.disabled = false;
            spinner?.classList.add("d-none");
            buttonText?.classList.remove("hidden");
        }
    }

    private getFormData(form: HTMLFormElement): { [key: string]: string } {
        const formData = new FormData(form);
        const indexedArray: { [key: string]: string } = {};

        // Use getAll to handle ASP.NET's checkbox pattern, which renders both a
        // checkbox (value="true") and a hidden field (value="false") with the same
        // name. A plain forEach would let the hidden "false" always overwrite the
        // checkbox "true". Instead, prefer "true" if it appears among the values.
        for (const key of new Set(formData.keys())) {
            const values = formData.getAll(key).map(v => v.toString());
            indexedArray[key] = values.includes('true') ? 'true' : values[values.length - 1];
        }

        return indexedArray;
    }

    private getBookingFormData(form: HTMLFormElement): Record<string, any> {
        const fd = new FormData(form);
        const data: Record<string, any> = {};

        // 1) pull raw values (strings)
        for (const [key, value] of fd.entries()) {
            // ignore files here
            if (value instanceof File) continue;

            // If you have multi-select/checkbox groups with same name, support arrays:
            if (key in data) {
                data[key] = Array.isArray(data[key]) ? [...data[key], value] : [data[key], value];
            } else {
                data[key] = value;
            }
        }

        // 2) helpers
        const toBool = (v: any) => {
            if (typeof v === "boolean") return v;
            if (typeof v === "string") return v.toLowerCase() === "true" || v === "on" || v === "1";
            if (typeof v === "number") return v === 1;
            return false;
        };

        const toNumber = (v: any) => {
            if (typeof v === "number") return v;
            if (typeof v !== "string") return v;
            const n = Number(v);
            return Number.isFinite(n) ? n : v; // if not numeric, leave as-is
        };

        // 3) coerce known numeric fields (add/remove as needed)
        const numberFields = [
            "Total",
            "SubTotal",
            "TotalVat",
            "EarlyBirdDiscountTotal",
            "DiscountTotal",
            "TotalBookings",
            "PaymentMethod", 
            "ShippingCost"
        ];

        for (const f of numberFields) {
            if (data[f] !== undefined && data[f] !== null && data[f] !== "") {
                data[f] = toNumber(data[f]);
            }
        }

        // 4) coerce known boolean fields (add/remove as needed)
        const boolFields = [
            "SameAsAbove",
            "InvoiceTermsAgreed",
            "PayByCard",
            "PayByInvoice",
            "ChargedToOrganisationAvailable",
            "ChargeToOrganisation",
            "TermsAllAgreed",
            "BillingAddressDetailsAreTheSame",
            "RequiresShipping",
            "RequiresShippingTelephone",
            "AgreeToTermsAndConditions"

        ];

        for (const f of boolFields) {
            if (data[f] !== undefined && data[f] !== null && data[f] !== "") {
                data[f] = toBool(data[f]);
            }
        }

        return data;
    }

}

